import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorBoundary from './utils/errorBoundary';

// Lazy-loaded components
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const BusinessSetup = lazy(() => import('./pages/BusinessSetup'));
const InvitePage = lazy(() => import('./pages/InvitePage'));
const PublicFormView = lazy(() => import('./components/PublicFormView'));
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const POSPage = lazy(() => import('./pages/POSPage'));
const SuccessPage = lazy(() => import('./components/SuccessPage'));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BusinessProvider>
            <Router>
              <div className="min-h-screen bg-gray-50">
                <Suspense fallback={<LoadingSpinner text="Loading..." />}>
                  <Routes>
                    <Route path="/" element={<LandingPage />} />
                    <Route path="/invite/:token" element={<InvitePage />} />
                    <Route path="/form/:publicUrl" element={<PublicFormView />} />
                    <Route 
                      path="/setup" 
                      element={
                        <ProtectedRoute>
                          <BusinessSetup />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/dashboard" 
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/success" 
                      element={
                        <ProtectedRoute>
                          <SuccessPage />
                        </ProtectedRoute>
                      } 
                    />
                    <Route 
                      path="/pos" 
                      element={
                        <ProtectedRoute>
                          <POSPage />
                        </ProtectedRoute>
                      } 
                    />
                  </Routes>
                </Suspense>
              </div>
            </Router>
          </BusinessProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;